import React, { useState, useEffect, useCallback } from 'react'
import { Table } from 'react-bootstrap'
import { FaSearch } from 'react-icons/fa'
import API from '../../../service/api'
import { customToast } from '../../../utils/toasts'
import './UserManagement.css'

const UserManagement = () => {
  const [users, setUsers] = useState([])
  const [searchKey, setSearchKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const ITEMS_PER_PAGE = 15

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true)
      const res = await API({ url: '/user/all-users-manage', method: 'GET', params: { searchKey }, manageLoader: false })
      setUsers(res?.data?.data || [])
    } catch (err) {
      console.error('Failed to fetch users', err)
    } finally {
      setLoading(false)
    }
  }, [searchKey])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers()
    }, 400)
    return () => clearTimeout(timer)
  }, [fetchUsers])

  const handleToggleStatus = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate'
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return
    try {
      const res = await API({ url: `/user/toggle-status/${userId}`, method: 'PUT', manageLoader: false })
      if (res?.data?.data) {
        customToast({ message: `User ${action}d successfully` })
        fetchUsers()
      }
    } catch (err) {
      console.error('Toggle status failed', err)
    }
  }

  const getTimeDiff = (lastActive) => {
    if (!lastActive) return 'Never'
    const diff = Math.floor((Date.now() - new Date(lastActive).getTime()) / 60000)
    if (diff < 1) return 'Just now'
    if (diff < 60) return `${diff}m ago`
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`
    return `${Math.floor(diff / 1440)}d ago`
  }

  const totalPages = Math.ceil(users.length / ITEMS_PER_PAGE)
  const paginatedUsers = users.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  const activeCount = users.filter(u => u.isActive).length
  const inactiveCount = users.filter(u => !u.isActive).length

  return (
    <div className='um-container'>
      <div className='um-header'>
        <h5 className='um-title'>User Management</h5>
        <div className='um-stats'>
          <span className='um-badge um-badge-active'>{activeCount} Active</span>
          <span className='um-badge um-badge-inactive'>{inactiveCount} Inactive</span>
          <span className='um-badge um-badge-total'>{users.length} Total</span>
        </div>
      </div>

      <div className='um-toolbar'>
        <div className='um-search-box'>
          <FaSearch className='um-search-icon' />
          <input
            type='text'
            placeholder='Search by name or email...'
            value={searchKey}
            onChange={(e) => { setSearchKey(e.target.value); setCurrentPage(1) }}
            className='um-search-input'
          />
        </div>
        <button className='um-refresh-btn' onClick={fetchUsers} disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className='um-table-wrapper'>
        <Table bordered hover size='sm' className='um-table'>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Role</th>
              <th>Department</th>
              <th>Location</th>
              <th>Status</th>
              <th>Last Active</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 && (
              <tr><td colSpan={10} className='text-center'>Loading...</td></tr>
            )}
            {!loading && users.length === 0 && (
              <tr><td colSpan={10} className='text-center'>No users found</td></tr>
            )}
            {paginatedUsers.map((u, i) => (
              <tr key={u._id} className={!u.isActive ? 'um-row-inactive' : ''}>
                <td>{(currentPage - 1) * ITEMS_PER_PAGE + i + 1}</td>
                <td>{u.name || '-'}</td>
                <td>{u.email || '-'}</td>
                <td>{u.mobileNumber || '-'}</td>
                <td><span className='um-role-badge'>{u.roleName || '-'}</span></td>
                <td>{u.departmentName || '-'}</td>
                <td>{u.location || '-'}</td>
                <td>
                  <span className={`um-status ${u.isActive ? 'um-status-active' : 'um-status-inactive'}`}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{getTimeDiff(u.lastActiveAt)}</td>
                <td>
                  <button
                    className={`um-toggle-btn ${u.isActive ? 'um-btn-deactivate' : 'um-btn-activate'}`}
                    onClick={() => handleToggleStatus(u._id, u.isActive)}
                  >
                    {u.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className='um-pagination'>
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  )
}

export default UserManagement
